import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Observable, of } from 'rxjs';

import { EASButtonAction, EASLinkAction } from '@fry/components/common/buttons/buttons.service';
import { SecurityService } from '@fry/lib/security';
import { BookingItem, BookingItemsStore } from '@fry/booking/lib/booking-items';
import { NotifyService } from '@fry/lib/utils';
import { BookingOptionDoc } from '@fry/booking/lib/booking-items/booking-option';
// import { ProfileStore } from '@fry/lib/profile';
import { LanguageService } from '@fry/lib/organisations/language.service';

@Component({
    selector: 'eas-booking-item-slot',
    templateUrl: './booking-item-slot.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BookingItemSlotComponent implements OnInit {
  @Input() bookingItem: BookingItem;

  public buttons: (EASButtonAction | EASLinkAction)[];
  public bookingOption$: Observable<BookingOptionDoc>;

  constructor(
    private security: SecurityService,
    private bookingItems: BookingItemsStore,
    private notify: NotifyService,
    private language: LanguageService,
  ) { }

  ngOnInit() {
    this.buttons = this.getWorkflowButtons();
    const fltr = {
      omitParts: ['form', 'eligibility'],
      path: []
    };
    this.bookingOption$ = this.bookingItems.getOptionsHierarchy(this.bookingItem.id, fltr);
  }

  getVariant(range) {
    if (!range || !range.min) {
      return '';
    }

    const now = new Date().toISOString();
    if (range.min < now && range.max > now) {
      return 'warn';
    }

    return '';

  }

  getWorkflowButtons() {
    const originalLanguageCode = this.language.getOriginalLanguageCode();
    const translationLanguageCodes = this.language.getTranslationLanguageCodes();

    const translateButtons: EASLinkAction[] = translationLanguageCodes.map(translationLanguageCode => ({
      type: 'link',
      label: $localize `Edit translation (${translationLanguageCode})`,
      condition: () => {
        return this.security.hasPermission('translations.view');
      },
      link: of(['/', 'translation', this.bookingItem.doc.type, this.bookingItem.doc._id, translationLanguageCode])
    }));

    const buttons: (EASButtonAction | EASLinkAction)[] = [
      {
        type: 'link',
        label: translationLanguageCodes.length === 0 ? $localize `Edit` : $localize `Edit original (${originalLanguageCode})` ,
        condition: () => {
          let securityContext = this.bookingItem.doc?.securityContext ?? { roles: [] };
          return this.security.hasPermission('bookingItems.edit', securityContext);
        },
        link: of([this.bookingItem.doc._id, 'edit'])
      },
      ...translateButtons,
      {
        type: 'link',
        label: $localize `Integration info`,
        condition: () => {
          return of(true);
        },
        link: of([this.bookingItem.doc._id, 'integration'])
      },
      {
        type: 'link',
        label: $localize `Duplicate`,
        condition: () => {
          let securityContext = this.bookingItem.doc?.securityContext ?? { roles: [] };
          return this.security.hasPermission('bookingItems.edit', securityContext);
        },
        link: of(['add']),
        queryParams: { duplicate: this.bookingItem.doc._id },
      },
      {
        type: 'button',
        label: $localize `Publish`,
        condition: () => {
          if (this.bookingItem.workflow.state !== 'draft') {
            return of(false);
          }
          let securityContext = this.bookingItem.doc?.securityContext ?? { roles: [] };
          return this.security.hasPermission('bookingItems.edit', securityContext);
        },
        click: () => {
          const orig = this.bookingItem.doc.workflow;
          return this.bookingItems.doTransition(this.bookingItem, 'publish').subscribe({
            next: (item) => {
              this.bookingItem.doc._rev = item.doc._rev;
              this.bookingItem.doc.workflow = item.doc.workflow;
              this.buttons = this.getWorkflowButtons();
            },
            error: err => {
              this.bookingItem.doc.workflow = orig;
              this.notify.error(err);
            }
          });
        }
      },
      {
        type: 'button',
        label: $localize `Archive`,
        condition: () => {
          if (this.bookingItem.workflow.state !== 'published') {
            return of(false);
          }
          let securityContext = this.bookingItem.doc?.securityContext ?? { roles: [] };
          return this.security.hasPermission('bookingItems.edit', securityContext);
        },
        click: () => {
          const orig = this.bookingItem.doc.workflow;
          return this.bookingItems.doTransition(this.bookingItem, 'archive').subscribe({
            next: (item) => {
              this.bookingItem.doc._rev = item.doc._rev;
              this.bookingItem.doc.workflow = item.doc.workflow;
              this.buttons = this.getWorkflowButtons();
            },
            error: err => {
              this.bookingItem.doc.workflow = orig;
              this.notify.error(err);
            }
          });
         }
      },
      {
        type: 'button',
        label: $localize `Move to draft`,
        condition: () => {
          if (this.bookingItem.workflow.state !== 'archived') {
            return of(false);
          }
          let securityContext = this.bookingItem.doc?.securityContext ?? { roles: [] };
          return this.security.hasPermission('bookingItems.edit', securityContext);
        },
        click: () => {
          const orig = this.bookingItem.doc.workflow;
          return this.bookingItems.doTransition(this.bookingItem, 'retract').subscribe({
            next: (item) => {
              this.bookingItem.doc._rev = item.doc._rev;
              this.bookingItem.doc.workflow = item.doc.workflow;
              this.buttons = this.getWorkflowButtons();
            },
            error: err => {
              this.bookingItem.doc.workflow = orig;
              this.notify.error(err);
            }
          });
        }
      },
    ];
    return buttons;
  }
}
